import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import cookieService from '@/services/cookie';
import api from '@/services/api';
import router from '@/router';

// Aumentado de 30 minutos para 2 horas para reduzir chamadas à API
const CACHE_DURATION = 2 * 60 * 60 * 1000; // 2 horas em millisegundos
// Tempo máximo para considerar os dados "stale" e forçar atualização
const MAX_STALE_DURATION = 8 * 60 * 60 * 1000; // 8 horas

export const useAuthStore = defineStore('auth', () => {
  // Estado
  const user = ref(cookieService.getUserData() || null);
  const loading = ref(false);
  const error = ref(null);
  const lastFetch = ref(0);
  const fetchPromise = ref(null);
  const isRefreshingUserData = ref(false);

  // Getters
  const isAuthenticated = computed(
    () => !!user.value && cookieService.isAuthenticated()
  );
  const userFullName = computed(() =>
    user.value ? `${user.value.nome} ${user.value.sobrenome}` : ''
  );

  // Verificar se o cache está válido (dados frescos)
  const isCacheValid = computed(() => {
    const now = Date.now();
    return lastFetch.value > 0 && now - lastFetch.value < CACHE_DURATION;
  });

  // Verificar se os dados estão "stale" mas ainda utilizáveis
  const hasStaleData = computed(() => {
    const now = Date.now();
    return (
      lastFetch.value > 0 &&
      now - lastFetch.value >= CACHE_DURATION &&
      now - lastFetch.value < MAX_STALE_DURATION
    );
  });

  // Correção: verificar o caminho correto para o nome da role com tratamento de nulos
  const userRole = computed(() => {
    // Se não houver usuário, retorna null
    if (!user.value) {
      return null;
    }

    // Se tiver accountRole completo, usa ele
    if (user.value.accountRole?.name) {
      return user.value.accountRole.name;
    }

    // Se tiver apenas o accountRoleId, tenta determinar a role
    if (user.value.role) {
      return user.value.role === 'ADMIN' ? 'ADMIN' : 'USER';
    }

    // Se não tiver nenhuma informação de role
    console.warn('Dados de role não encontrados no usuário:', user.value);
    return null;
  });

  const userPermissions = computed(() => user.value?.permissions || []);

  const isAdmin = computed(() => {
    // Se não houver usuário ou role, não é admin
    if (!user.value || !userRole.value) {
      return false;
    }

    const isAdminByRole = userRole.value === 'ADMIN';
    const isAdminById =
      user.value.accountRole?.id === '8e56078d-962e-49be-91c7-d510b4b4879e';
    const isAdminByRoleId =
      user.value.accountRoleId === '8e56078d-962e-49be-91c7-d510b4b4879e';

    return isAdminByRole || isAdminById || isAdminByRoleId;
  });

  // Função para atualizar dados do usuário
  const updateUserData = (userData) => {
    if (!userData) {
      console.warn('Tentativa de atualizar dados do usuário com valor nulo');
      return;
    }

    // Garantir que o accountRole esteja incluído no userData
    if (userData.accountRoleId && !userData.accountRole) {
      userData.accountRole = {
        id: userData.accountRoleId,
        name:
          userData.accountRoleId === '8e56078d-962e-49be-91c7-d510b4b4879e'
            ? 'ADMIN'
            : 'USER',
      };
    }

    user.value = userData;
    cookieService.setUserData(userData);
    lastFetch.value = Date.now();
  };

  // Função para invalidar cache
  const invalidateCache = () => {
    lastFetch.value = 0;
    fetchPromise.value = null;
  };

  // Função principal para obter dados do usuário - refatorada com stale-while-revalidate
  const refreshUserData = async (force = false) => {
    // Se não estiver autenticado, não tenta buscar dados
    if (!cookieService.isAuthenticated()) {
      return false;
    }

    // Se já existe uma requisição em andamento, retorna ela
    if (fetchPromise.value) {
      return fetchPromise.value;
    }

    // Estratégia stale-while-revalidate:
    // 1. Se o cache é válido OU temos dados stale e não é forçado, retorna dados atuais
    // 2. Se temos dados stale e não estamos já buscando, busca em background
    // 3. Se não temos dados válidos nem stale, ou é forçado, busca e aguarda

    // Caso 1: Cache válido, retorna imediatamente
    if (!force && isCacheValid.value && user.value) {
      return user.value;
    }

    // Caso 2: Dados stale mas utilizáveis, inicia atualização em background e retorna dados stale
    if (
      !force &&
      hasStaleData.value &&
      user.value &&
      !isRefreshingUserData.value
    ) {
      // Iniciar atualização em background
      isRefreshingUserData.value = true;

      api.accounts
        .getMe()
        .then((response) => {
          if (response.data?.success && response.data?.data) {
            const userData = response.data.data;
            updateUserData(userData);
          }
        })
        .catch((err) => {
          console.error(
            'Erro ao atualizar dados do usuário em background:',
            err
          );
          if (err.response?.status === 401) {
            logout();
          }
        })
        .finally(() => {
          isRefreshingUserData.value = false;
        });

      // Retorna dados stale imediatamente
      return user.value;
    }

    // Caso 3: Sem dados válidos/stale ou atualização forçada
    loading.value = true;
    error.value = null;

    // Criar nova Promise para a requisição
    fetchPromise.value = api.accounts
      .getMe()
      .then((response) => {
        if (response.data?.success && response.data?.data) {
          const userData = response.data.data;
          updateUserData(userData);
          return userData;
        }
        throw new Error('Formato de resposta inválido');
      })
      .catch((err) => {
        console.error('Erro ao atualizar dados do usuário:', err);
        error.value = err.message;
        if (err.response?.status === 401) {
          logout();
        }
        return null;
      })
      .finally(() => {
        loading.value = false;
        fetchPromise.value = null;
      });

    return fetchPromise.value;
  };

  // Variável para armazenar o timer de refresh
  let refreshTokenTimer = null;
  // Variáveis para controlar o backoff exponencial
  let refreshAttempts = 0;
  let isRefreshingToken = false;
  const MAX_REFRESH_ATTEMPTS = 5;
  const BASE_BACKOFF_TIME = 5000; // 5 segundos

  // Definir o store antes para poder usar nas funções
  let authStore = null;

  // Adicionar lógica para configurar o timer de refresh automático com backoff exponencial
  const setupTokenRefreshTimer = () => {
    // Cancelar qualquer timer existente
    if (refreshTokenTimer) {
      clearTimeout(refreshTokenTimer);
      refreshTokenTimer = null;
    }

    // Redefinir contagem de tentativas se estamos configurando um novo timer
    refreshAttempts = 0;

    const token = localStorage.getItem('accessToken');
    if (!token) return;

    try {
      // Decodificar o token para obter o tempo de expiração
      const tokenData = JSON.parse(atob(token.split('.')[1]));
      const expirationTime = tokenData.exp * 1000; // Converter para milissegundos
      const currentTime = Date.now();

      // Calcular tempo para expiração (com margem de segurança de 5 minutos)
      const timeUntilExpiry = expirationTime - currentTime - 5 * 60 * 1000;

      // Se o token já está perto de expirar ou expirado, renovar imediatamente
      if (timeUntilExpiry <= 0) {
        scheduleRefreshToken(0);
        return;
      }

      // Configurar timer para renovar antes da expiração
      refreshTokenTimer = setTimeout(() => {
        scheduleRefreshToken(0);
      }, timeUntilExpiry);
    } catch (error) {
      console.error('[Auth Store] Erro ao configurar timer de refresh:', error);
    }
  };

  // Nova função para agendar um refresh com backoff exponencial
  const scheduleRefreshToken = (retryDelay = 0) => {
    // Se já estamos atualizando, não agende novamente
    if (isRefreshingToken) return;

    // Se atingimos o máximo de tentativas, desistir
    if (refreshAttempts >= MAX_REFRESH_ATTEMPTS) {
      console.error(
        `[Auth Store] Máximo de tentativas de refresh (${MAX_REFRESH_ATTEMPTS}) atingido.`
      );
      logout();
      return;
    }

    // Cancelar qualquer timer existente
    if (refreshTokenTimer) {
      clearTimeout(refreshTokenTimer);
      refreshTokenTimer = null;
    }

    // Configurar o timer com atraso exponencial
    refreshTokenTimer = setTimeout(async () => {
      try {
        isRefreshingToken = true;

        // Se não houver authStore, obtenha-o
        if (!authStore) {
          authStore = useAuthStore();
        }

        await authStore.refreshToken();

        // Reset de tentativas após sucesso
        refreshAttempts = 0;
        isRefreshingToken = false;
      } catch (error) {
        // Incrementar tentativas e calcular próximo atraso
        refreshAttempts++;
        const nextDelay = calculateBackoffDelay(refreshAttempts);

        console.warn(
          `[Auth Store] Tentativa ${refreshAttempts} de refresh do token falhou. Próxima tentativa em ${
            nextDelay / 1000
          }s.`
        );
        isRefreshingToken = false;

        // Programar próxima tentativa
        scheduleRefreshToken(nextDelay);
      }
    }, retryDelay);
  };

  // Função para calcular atraso de backoff exponencial
  const calculateBackoffDelay = (attempt) => {
    // Exponential backoff with jitter: 2^n * BASE_TIME + random jitter
    const jitter = Math.random() * 1000; // Jitter de até 1 segundo
    return Math.min(
      Math.pow(2, attempt) * BASE_BACKOFF_TIME + jitter,
      5 * 60 * 1000
    ); // Max 5 minutos
  };

  // Ações
  async function login(credentials) {
    loading.value = true;
    error.value = null;

    try {
      const response = await api.auth.login(credentials);

      if (response.data && response.data.success && response.data.data) {
        const {
          accessToken,
          refreshToken,
          user: userData,
        } = response.data.data;

        // Primeiro salvamos os tokens
        cookieService.setAccessToken(accessToken);
        cookieService.setRefreshToken(refreshToken);

        // Atualizamos os dados do usuário
        updateUserData(userData);

        // Forçamos uma atualização dos dados do usuário para garantir que temos as informações mais recentes
        await refreshUserData(true);

        router.push({ name: 'suppliers' });

        // Após login bem-sucedido, configurar o timer de refresh
        setupTokenRefreshTimer();

        return true;
      } else {
        throw new Error('Formato de resposta inválido');
      }
    } catch (err) {
      // Extrair a mensagem de erro da resposta aninhada
      if (err.response?.data?.error?.details?.details?.error?.details?.message) {
        error.value = err.response.data.error.details.details.error.details.message;
      } else if (err.response?.data?.error?.details?.message) {
        error.value = err.response.data.error.details.message;
      } else if (err.response?.data?.error?.message) {
        error.value = err.response.data.error.message;
      } else if (err.response?.data?.message) {
        error.value = err.response.data.message;
      } else {
        error.value = err.message || 'Falha na autenticação.';
      }

      return false;
    } finally {
      loading.value = false;
    }
  }

  async function logout() {
    try {
      // Limpar todos os dados locais
      cookieService.clearAll();
      localStorage.removeItem('currentUser');
      localStorage.removeItem('locationSkipped');
      user.value = null;
      invalidateCache();

      // Limpar o timer de refresh quando o usuário faz logout
      if (refreshTokenTimer) {
        clearTimeout(refreshTokenTimer);
        refreshTokenTimer = null;
      }

      // Redirecionar para a página de login se não estivermos na página de login
      if (router.currentRoute.value.name !== 'login') {
        router.push({ name: 'login' });
      }
    } catch (err) {
      // Mesmo em caso de erro, garantimos que o usuário seja redirecionado
      if (router.currentRoute.value.name !== 'login') {
        router.push({ name: 'login' });
      }
    }
  }

  async function refreshToken() {
    // Se já estamos tentando renovar, não faça nada
    if (isRefreshingToken) {
      return { success: false, error: 'refresh_in_progress' };
    }

    isRefreshingToken = true;

    try {
      const refreshToken = cookieService.getRefreshToken();

      if (!refreshToken) {
        // Tentar recuperar do localStorage como fallback
        const lsToken = localStorage.getItem('refreshToken');
        if (lsToken) {
          cookieService.setRefreshToken(lsToken);
        } else {
          console.error('[Auth] Refresh token não encontrado em nenhum lugar');
        }

        // Limpar os dados de autenticação sem tentar fazer logout no servidor
        // Isso evita o loop infinito de tentativas de logout que também falham
        cookieService.clearAll();
        user.value = null;
        invalidateCache();

        // Redirecionar para login com mensagem específica
        if (router.currentRoute.value.name !== 'login') {
          router.push({
            name: 'login',
            query: {
              redirect: router.currentRoute.value.fullPath,
              error: 'session_expired',
            },
          });
        }

        isRefreshingToken = false;
        return { success: false, error: 'refresh_token_missing' };
      }

      // Verificar se o token é uma string válida
      if (typeof refreshToken !== 'string' || !refreshToken.trim()) {
        console.error('[Auth] Refresh token inválido:', refreshToken);

        // Limpar dados inválidos e redirecionar para login
        cookieService.clearAll();
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('accessToken');
        user.value = null;
        invalidateCache();

        isRefreshingToken = false;
        return { success: false, error: 'invalid_refresh_token' };
      }

      // Enviar token no formato que o backend espera
      const response = await api.auth.refreshToken(refreshToken);

      if (response.data && response.data.success && response.data.data) {
        const {
          accessToken,
          refreshToken: newRefreshToken,
          user: userData,
        } = response.data.data;

        cookieService.setAccessToken(accessToken);

        // Salvar também no localStorage como backup
        localStorage.setItem('accessToken', accessToken);

        if (newRefreshToken) {
          cookieService.setRefreshToken(newRefreshToken);
          localStorage.setItem('refreshToken', newRefreshToken);
        }

        if (userData) {
          updateUserData(userData);
        }

        // Reconfigurar timer após refresh bem-sucedido
        refreshAttempts = 0;
        setupTokenRefreshTimer();

        isRefreshingToken = false;
        return { success: true };
      } else {
        console.error('[Auth] Resposta inválida do servidor:', response.data);
        throw new Error(
          'Resposta inválida do servidor durante refresh do token'
        );
      }
    } catch (error) {
      console.error('[Auth] Erro ao renovar token:', error);

      isRefreshingToken = false;

      // Se for um erro 429 (too many requests), programar retry com backoff
      if (error.response && error.response.status === 429) {
        refreshAttempts++;
        const retryDelay = calculateBackoffDelay(refreshAttempts);
        console.warn(
          `[Auth] Rate limit atingido. Tentativa ${refreshAttempts}. Próxima tentativa em ${
            retryDelay / 1000
          }s.`
        );

        // Agendar próxima tentativa
        scheduleRefreshToken(retryDelay);
        return { success: false, error: 'rate_limited' };
      }

      // Vamos usar um método diferente de logout para evitar loops
      // Não tentaremos fazer logout no servidor, apenas limparemos os dados locais
      cookieService.clearAll();
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      user.value = null;
      invalidateCache();

      // Redirecionar para login com mensagem específica
      if (router.currentRoute.value.name !== 'login') {
        router.push({
          name: 'login',
          query: {
            redirect: router.currentRoute.value.fullPath,
            error: 'auth_refresh_failed',
          },
        });
      }

      return { success: false, error: 'refresh_failed' };
    }
  }

  async function register(userData) {
    loading.value = true;
    error.value = null;

    try {
      const response = await api.auth.register(userData);

      // Processamento semelhante ao login quando o registro é bem-sucedido
      if (response.data && response.data.success && response.data.data) {
        const {
          accessToken,
          refreshToken,
          user: userData,
        } = response.data.data;

        // Salvar tokens nos cookies
        cookieService.setAccessToken(accessToken);
        cookieService.setRefreshToken(refreshToken);

        // Garantir que o accountRole esteja incluído no userData
        if (userData.accountRoleId && !userData.accountRole) {
          // Se temos apenas o accountRoleId mas não o objeto accountRole,
          // vamos simular o objeto para manter a consistência
          userData.accountRole = {
            id: userData.accountRoleId,
            name:
              userData.accountRoleId === '8e56078d-962e-49be-91c7-d510b4b4879e'
                ? 'ADMIN'
                : 'USER',
          };
        }

        // Salvar no cookie
        cookieService.setUserData(userData);

        // Atualizar o estado
        user.value = userData;

        // Atualizar o timestamp de última atualização
        lastFetch.value = Date.now();

        return true;
      }

      return response.data;
    } catch (err) {
      error.value = err.response?.data?.message || 'Erro ao registrar usuário.';
      throw error.value;
    } finally {
      loading.value = false;
    }
  }

  async function updateProfile(profileData) {
    loading.value = true;
    error.value = null;

    try {
      const response = await api.accounts.update(user.value.id, profileData);
      const updatedUser = response.data.data;

      // Atualizar cookies e estado
      cookieService.setUserData(updatedUser);
      user.value = updatedUser;

      return updatedUser;
    } catch (err) {
      error.value = err.response?.data?.message || 'Erro ao atualizar perfil.';
      throw error.value;
    } finally {
      loading.value = false;
    }
  }

  async function changePassword(passwordData) {
    loading.value = true;
    error.value = null;

    try {
      await api.accounts.changePassword(passwordData);
      return true;
    } catch (err) {
      error.value = err.response?.data?.message || 'Erro ao alterar senha.';
      throw error.value;
    } finally {
      loading.value = false;
    }
  }

  async function forgotPassword(email) {
    loading.value = true;
    error.value = null;

    try {
      const response = await api.auth.forgotPassword(email);

      if (response.data?.message) {
        return {
          success: true,
          message: response.data.message,
        };
      }

      return {
        success: true,
        message:
          'Instruções para redefinição de senha foram enviadas para seu email',
      };
    } catch (err) {
      error.value =
        err.response?.data?.message ||
        'Erro ao solicitar recuperação de senha.';
      return {
        success: false,
        message: error.value,
      };
    } finally {
      loading.value = false;
    }
  }

  async function resetPassword(resetData) {
    loading.value = true;
    error.value = null;

    try {
      // Formatar os dados para o formato esperado pela API
      const payload = {
        token: resetData.token, // Este deve ser o token JWT, não o token hash
        newPassword: resetData.password, // Adapta do formato do frontend para o backend
      };

      const response = await api.auth.resetPassword(payload);

      if (response.data && response.data.success) {
        // Verificar se a resposta contém novos tokens de acesso
        const {
          accessToken,
          refreshToken,
          user: userData,
        } = response.data.data || {};

        // Se recebemos tokens novos, vamos atualizá-los
        if (accessToken) {
          cookieService.setAccessToken(accessToken);
          localStorage.setItem('accessToken', accessToken);
        }

        if (refreshToken) {
          cookieService.setRefreshToken(refreshToken);
          localStorage.setItem('refreshToken', refreshToken);
        }

        // Se temos dados do usuário, atualizamos o estado
        if (userData) {
          updateUserData(userData);
        }

        // Limpar o token temporário de reset
        localStorage.removeItem('password_reset_token');

        return {
          success: true,
          message: response.data.message || 'Senha redefinida com sucesso.',
        };
      }

      return {
        success: false,
        message: response.data?.message || 'Erro ao redefinir senha.',
      };
    } catch (err) {
      console.error('Erro ao redefinir senha:', err);
      const errorMessage =
        err.response?.data?.message || 'Erro ao redefinir senha.';
      error.value = errorMessage;
      return {
        success: false,
        message: errorMessage,
      };
    } finally {
      loading.value = false;
    }
  }

  // Validar token de redefinição de senha
  async function validateResetToken(token) {
    loading.value = true;
    error.value = null;

    try {
      const response = await api.auth.validateResetToken(token);

      if (response.data && response.data.success) {
        // A estrutura da resposta é mais aninhada do que esperávamos:
        // response.data.data.data.token
        const userData = response.data.data?.data?.user || {};
        const validationToken = response.data.data?.data?.token;

        // Armazenar temporariamente o token JWT para uso na redefinição de senha
        if (validationToken) {
          localStorage.setItem('password_reset_token', validationToken);
        }

        return {
          success: true,
          message: response.data.data?.message || 'Token válido.',
          user: userData,
          token: validationToken,
        };
      }

      return {
        success: false,
        message: response.data?.message || 'Token inválido ou expirado.',
      };
    } catch (err) {
      console.error('Erro ao validar token de redefinição:', err);
      const errorMessage =
        err.response?.data?.message || 'Erro ao validar token.';
      error.value = errorMessage;
      return {
        success: false,
        message: errorMessage,
      };
    } finally {
      loading.value = false;
    }
  }

  // Verificar e configurar refresh token no carregamento inicial da aplicação
  if (typeof window !== 'undefined') {
    // Atribuir a instância do store para uso nas funções
    authStore = useAuthStore();

    const token = localStorage.getItem('accessToken');
    if (token && authStore) {
      setupTokenRefreshTimer();
    }
  }

  // Adicionar método para limpar dados de autenticação
  const clearAuthData = () => {
    // Limpar dados no localStorage
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');

    // Limpar dados no state
    user.value = null;
    invalidateCache();

    // Limpar timer de refresh
    if (refreshTokenTimer) {
      clearTimeout(refreshTokenTimer);
      refreshTokenTimer = null;
    }
  };

  // Adicionar novos métodos para suporte ao Magic Link
  // Estes devem ser adicionados junto com os outros actions no seu store

  // Dentro do objeto actions ou no grupo de métodos da store
  // validateMagicLink: Método para validar o token do Magic Link com o backend
  async function validateMagicLink(tokenHash) {
    loading.value = true;
    error.value = '';

    try {
      const response = await api.auth.validateMagicLink(tokenHash);

      if (response.data?.success) {
        // Verificar se temos os dados e tokens necessários
        const userData = response.data.data?.user;
        const accessToken =
          response.data.data?.accessToken ||
          response.data.data?.session?.access_token;
        const refreshToken =
          response.data.data?.refreshToken ||
          response.data.data?.session?.refresh_token;

        // Verificar se temos os dados essenciais
        if (!userData || !accessToken) {
          console.error('[Auth] Dados incompletos na resposta:', response.data);
          return {
            success: false,
            error: 'Dados de autenticação incompletos',
          };
        }

        // Atualizar tokens nos cookies
        cookieService.setAccessToken(accessToken);
        if (refreshToken) {
          cookieService.setRefreshToken(refreshToken);
        }

        // Atualizar dados do usuário
        updateUserData(userData);

        // Configurar timer de refresh token
        setupTokenRefreshTimer();

        // Retornamos o success junto com a flag mustChangePassword
        return {
          success: true,
          mustChangePassword: !!userData.mustChangePassword,
        };
      }

      // Se chegou aqui, houve algum problema com a resposta
      console.error('[Auth] Resposta inválida do servidor:', response.data);
      return {
        success: false,
        error: response.data?.message || 'Falha na validação do token',
      };
    } catch (err) {
      console.error('Erro ao validar Magic Link:', err);

      // Tentar extrair mensagem de erro detalhada da resposta
      let errorMessage =
        'Falha ao validar o link de acesso. Ele pode ter expirado ou ser inválido.';

      if (err.response?.data?.error?.message) {
        errorMessage = err.response.data.error.message;
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      }

      error.value = errorMessage;
      return { success: false, error: errorMessage };
    } finally {
      loading.value = false;
    }
  }

  // Atualizar senha após login via Magic Link
  async function updatePassword(data) {
    loading.value = true;
    error.value = '';

    try {
      const response = await api.auth.updatePassword(data);

      if (response.data?.success) {
        // Se o usuário tiver a flag mustChangePassword, atualizamos o userData
        // para remover essa flag após a senha ser alterada com sucesso
        if (user.value && user.value.mustChangePassword) {
          const updatedUserData = {
            ...user.value,
            mustChangePassword: false,
          };
          updateUserData(updatedUserData);
        }

        return { success: true };
      }

      return {
        success: false,
        error: response.data?.message || 'Falha ao definir nova senha.',
      };
    } catch (err) {
      console.error('Erro ao atualizar senha:', err);

      // Tentar extrair mensagem de erro detalhada da resposta
      let errorMessage =
        'Falha ao definir nova senha. Por favor, tente novamente.';

      if (err.response?.data?.error?.message) {
        errorMessage = err.response.data.error.message;
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      }

      error.value = errorMessage;
      return { success: false, error: errorMessage };
    } finally {
      loading.value = false;
    }
  }

  // Atualizar senha no primeiro login (endpoint específico)
  async function updatePasswordFirstLogin(data) {
    loading.value = true;
    error.value = '';

    try {
      const response = await api.auth.updatePasswordFirstLogin({
        newPassword: data.password,
      });

      if (response.data?.success) {
        // A resposta tem uma estrutura aninhada: response.data.data.data
        // Ajustar o caminho para acessar os tokens corretamente
        const nestedData = response.data?.data?.data;
        const accessToken = nestedData?.accessToken;
        const refreshToken = nestedData?.refreshToken;
        const userData = nestedData?.user;

        if (accessToken && refreshToken) {
          // Atualizar tokens nos cookies
          cookieService.setAccessToken(accessToken);
          cookieService.setRefreshToken(refreshToken);

          // Configurar timer de refresh token
          setupTokenRefreshTimer();
        }

        // Atualizar dados do usuário se recebidos
        if (userData) {
          updateUserData(userData);
        }

        // Atualizar flag mustChangePassword no userData atual
        if (user.value) {
          const updatedUserData = {
            ...user.value,
            mustChangePassword: false,
          };
          updateUserData(updatedUserData);
        }

        return {
          success: true,
          receivedNewTokens: !!(accessToken && refreshToken),
        };
      }

      return {
        success: false,
        error: response.data?.message || 'Falha ao definir nova senha.',
      };
    } catch (err) {
      console.error('Erro ao atualizar senha no primeiro login:', err);

      // Tentar extrair mensagem de erro detalhada da resposta
      let errorMessage =
        'Falha ao definir nova senha. Por favor, tente novamente.';

      if (err.response?.data?.error?.message) {
        errorMessage = err.response.data.error.message;
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      }

      error.value = errorMessage;
      return { success: false, error: errorMessage };
    } finally {
      loading.value = false;
    }
  }

  // Solicitar Magic Link
  async function requestMagicLink(email) {
    loading.value = true;
    error.value = '';

    try {
      const response = await api.auth.requestMagicLink({ email });

      if (response.data?.success) {
        return true;
      }

      return false;
    } catch (err) {
      console.error('Erro ao solicitar Magic Link:', err);
      error.value =
        'Falha ao solicitar link de acesso. Por favor, tente novamente.';
      throw err;
    } finally {
      loading.value = false;
    }
  }

  // Reenviar Magic Link
  async function resendMagicLink(email) {
    loading.value = true;
    error.value = '';

    try {
      const response = await api.auth.requestMagicLink({ email });

      if (response.data?.success) {
        return {
          success: true,
          message: 'Um novo link de acesso foi enviado para seu email.',
        };
      }

      return {
        success: false,
        message: response.data?.message || 'Erro ao reenviar o link de acesso.',
      };
    } catch (err) {
      console.error('Erro ao reenviar Magic Link:', err);

      // Tentar extrair mensagem de erro mais específica
      let errorMessage = 'Erro ao reenviar o link de acesso.';

      if (err.response?.data?.error?.message) {
        errorMessage = err.response.data.error.message;
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      }

      return {
        success: false,
        message: errorMessage,
      };
    } finally {
      loading.value = false;
    }
  }

  // Verificar se um email já está registrado
  async function checkEmail(email) {
    loading.value = true;
    error.value = '';

    try {
      const response = await api.auth.checkEmail(email);
      return response.data;
    } catch (err) {
      console.error('Erro ao verificar email:', err);

      // Tratar diferentes erros de resposta
      if (err.response?.data?.error?.code === 'VALIDATION_ERROR') {
        error.value = err.response.data.error.message || 'Email inválido';
      } else if (err.response?.data?.error?.code === 'TOO_MANY_REQUESTS') {
        error.value =
          'Muitas requisições. Por favor, tente novamente mais tarde.';
      } else {
        error.value = 'Erro ao verificar disponibilidade do email.';
      }

      // Retornar o objeto de erro para que o componente possa tratar
      return (
        err.response?.data || {
          success: false,
          error: { message: error.value },
        }
      );
    } finally {
      loading.value = false;
    }
  }

  // Retornar as propriedades e métodos expostos
  return {
    // Estado
    user,
    loading,
    error,

    // Getters
    isAuthenticated,
    userFullName,
    userRole,
    userPermissions,
    isAdmin,
    isCacheValid,
    hasStaleData,

    // Ações
    login,
    logout,
    register,
    refreshUserData,
    refreshToken,
    invalidateCache,
    updateUserData,
    updateProfile,
    changePassword,
    forgotPassword,
    resetPassword,
    clearAuthData,
    validateMagicLink,
    updatePassword,
    requestMagicLink,
    resendMagicLink,
    checkEmail,
    updatePasswordFirstLogin,
    validateResetToken,
  };
});

export default useAuthStore;
